import { stringify } from 'query-string';
import React from 'react';
import { useHistory } from 'react-router';

import AuthLayout from '@dashboard/layouts/AuthLayout';

import storage from '@dashboard/services/storage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { tracking } from '@dashboard/lib/tracking';

import Form from '@dashboard/components/Formik/Form';
import Link from '@dashboard/components/Link';
import Meta from '@dashboard/components/Meta';
import { GoogleSignIn } from '@dashboard/components/GoogleSignIn';

import { signup } from './helpers';

export default function Signup() {
  const { setLogged } = useAuth();
  const history = useHistory();
  const { template: createFromTemplateId, page: createFromPageId } = useUrlQuery();

  const preserveQueryParams = baseUrl => {
    const currentSearch = window.location.search;
    const redirectTo = `${baseUrl}${currentSearch}`;

    return redirectTo;
  };

  return (
    <>
      <Meta
        meta={{
          title: 'Signup',
        }}
      >
        <link rel="canonical" href={`${window.location.origin}/signup`} />
      </Meta>

      <AuthLayout>
        <div className="text-center">
          <h1 className="text-4xl">Create your account</h1>
          <h3 className="text-lg mt-2 font-normal mb-4">Start your 7-day free trial</h3>
        </div>

        <div className="py-4">
          {/* Google Sign-In */}
          <div className="mb-6">
            <GoogleSignIn
              mode="signup"
              marketingConsent={true}
              onSuccess={result => {
                console.log('Google signup success:', result);

                // Track signup event for new users
                if (result.isNewUser) {
                  tracking.identifyUser(result.user._id);
                  tracking.trackEvent('Signup', {
                    currency: 'USD',
                    predicted_ltv: 600,
                    userId: result.user._id,
                    email: result.user.email,
                    method: 'google',
                  });
                  tracking.setUser({
                    $email: result.user.email,
                    $name: result.user.name,
                    $created: new Date(),
                    $seller: result.user.seller?._id,
                  });
                }
              }}
              onError={error => {
                console.error('Google signup error:', error);
              }}
            />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>

          <Form
            initialValues={{
              name: '',
              email: '',
              password: '',
              marketingConsent: true,
            }}
            onSubmit={async values => {
              const response = await signup({
                ...values,
                session: storage.session.getItem('CheckoutPage.sessionId'),
              });

              return {
                data: response.data,
                error: response.error || response.message,
                onSuccess: () => {
                  const { token, user } = response.data;

                  setLogged({
                    user: user,
                    seller: user.seller,
                    token: token,
                  });

                  tracking.identifyUser(user._id);
                  tracking.trackEvent('Signup', {
                    currency: 'USD',
                    predicted_ltv: 600,
                    userId: user._id,
                    email: user.email,
                  });
                  tracking.setUser({
                    $email: user.email,
                    $name: user.name,
                    $created: new Date(),
                    $seller: user.seller?._id,
                  });

                  history.push({
                    pathname: '/dashboard/seller/start',
                    search: stringify({
                      step: 1,
                      ...(createFromTemplateId && {
                        template: createFromTemplateId,
                      }),
                      ...(createFromPageId && {
                        page: createFromPageId,
                      }),
                    }),
                  });
                },
              };
            }}
            showState={false}
            fields={[
              {
                name: 'name',
                label: 'First name',
                placeholder: 'First name',
                required: true,
                type: 'text',
              },
              {
                name: 'email',
                label: 'Email',
                placeholder: 'Email',
                type: 'email',
                required: true,
                autocomplete: 'username',
              },
              {
                name: 'password',
                label: 'Password',
                placeholder: 'Password',
                type: 'password',
                required: true,
                autocomplete: 'new-password',
              },
            ]}
            submitButton={{
              label: 'Create account',
              appearance: 'primary',
              size: 'full',
            }}
          />
        </div>

        <div className="text-center mt-4">
          Already have an account?{' '}
          <Link href={preserveQueryParams('/login')} variant="secondary">
            Log in
          </Link>
        </div>
      </AuthLayout>
    </>
  );
}
